import apiService from './apiService';

/**
 * @typedef {{ id: number, label: string }} CatalogItem
 *
 * @typedef {{
 *   day   : string,   // 'Mon' | 'Tue' | … | 'Sun'
 *   block : string    // 'Morning' | 'Afternoon' | 'Evening' | 'Night'
 * }} AvailabilitySlot
 *
 * HelperProfileInput — sent to POST/PUT /users/profile
 * @typedef {{
 *   equipment    : number[],   // catalog IDs from GET /users/equipment
 *   skills       : number[],   // catalog IDs from GET /users/skills
 *   availability : AvailabilitySlot[]
 * }} HelperProfileInput
 *
 * HelperProfile — returned by GET /users/profile
 * @typedef {{
 *   equipment    : CatalogItem[],
 *   skills       : CatalogItem[],
 *   availability : AvailabilitySlot[]
 * }} HelperProfile
 */

class HelperService {
  /**
   * Fetch the equipment catalog (public — no auth required).
   * GET /users/equipment → CatalogItem[]
   * @returns {Promise<CatalogItem[]>}
   */
  async getEquipmentCatalog() {
    try {
      const data = await apiService.get('/users/equipment');
      return Array.isArray(data?.equipment) ? data.equipment : [];
    } catch (error) {
      console.warn('getEquipmentCatalog unavailable:', error.message);
      return [];
    }
  }

  /**
   * Fetch the skills catalog (public — no auth required).
   * GET /users/skills → CatalogItem[]
   * @returns {Promise<CatalogItem[]>}
   */
  async getSkillsCatalog() {
    try {
      const data = await apiService.get('/users/skills');
      return Array.isArray(data?.skills) ? data.skills : [];
    } catch (error) {
      console.warn('getSkillsCatalog unavailable:', error.message);
      return [];
    }
  }

  /**
   * Fetch the authenticated user's helper profile.
   *
   * GET /users/profile
   *
   * Returns HelperProfile on success, or null if no profile exists (404)
   * or the server is unreachable.
   *
   * @returns {Promise<HelperProfile|null>}
   */
  async getProfile() {
    try {
      return await apiService.get('/users/profile');
    } catch (error) {
      if (error.status === 404) return null;   // no profile yet — not an error
      console.warn('getProfile API unavailable:', error.message);
      return null;
    }
  }

  /**
   * Create the authenticated user's helper profile.
   *
   * POST /users/profile  (HTTP 201 on success)
   *
   * @returns {Promise<HelperProfile>}
   */
  async saveProfile(equipment, skills, availability, userFields = {}) {
    const payload = this._buildInput(equipment, skills, availability, userFields);
    try {
      return await apiService.post('/users/profile', payload);
    } catch (error) {
      console.warn('saveProfile API unavailable — using mock flow:', error.message);
      return { mock: true, message: 'Helper profile created (mock)' };
    }
  }

  /**
   * Replace the authenticated user's existing helper profile.
   *
   * PUT /users/profile  (HTTP 200 on success)
   *
   * @returns {Promise<HelperProfile>}
   */
  async updateProfile(equipment, skills, availability, userFields = {}) {
    const payload = this._buildInput(equipment, skills, availability, userFields);
    try {
      return await apiService.put('/users/profile', payload);
    } catch (error) {
      console.warn('updateProfile API unavailable — using mock flow:', error.message);
      return { mock: true, message: 'Helper profile updated (mock)' };
    }
  }

  // ---------------------------------------------------------------------------
  // Internal helpers
  // ---------------------------------------------------------------------------

  /**
   * Convert the page's local state into the wire-format payload.
   *
   * availability arg is the nested object built by initAvailability():
   *   { Mon: { Morning: true, Afternoon: false, … }, Tue: { … }, … }
   *
   * We flatten it to an array of only the selected slots so the payload
   * is compact and easy for the server to iterate.
   */
  /**
   * @param {object} userFields  Optional user-record fields to merge in.
   *   Supported by PUT /users/profile: name, phone, street, city, state, zip, country.
   *   Any present address field triggers a server-side re-geocode.
   */
  _buildInput(equipment, skills, availability, userFields = {}) {
    const selectedSlots = [];
    for (const [day, blocks] of Object.entries(availability)) {
      for (const [block, selected] of Object.entries(blocks)) {
        if (selected) selectedSlots.push({ day, block });
      }
    }

    return {
      ...userFields,
      equipment:    equipment,
      skills:       skills,
      availability: selectedSlots,
    };
  }
}

export default new HelperService();
